import { useEffect, useRef, useState } from 'react'

import isEqual from 'lodash/fp/isEqual'

import { GAP, ICON_WIDTH, MORE_ACTION_WIDTH, SEPARATOR_WIDTH } from './consts'
import { ActionItem } from './types'

interface Options {
  actions: ActionItem[]
}

export const useOverflowingActions = ({ actions }: Options) => {
  const [overflowingActions, setOverflowingActions] = useState<string[]>([])
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const container = containerRef.current
    const updateOverflowingActions = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        if (container && entry.target === container) {
          const containerRect = container.getBoundingClientRect()

          const { newOverflowingActions } = actions.reduce<{
            contentWidth: number
            newOverflowingActions: string[]
          }>(
            (
              { contentWidth, newOverflowingActions },
              { withSeparator, name },
            ) => {
              let actionItemWidth = ICON_WIDTH

              const visibleActions = actions.filter(
                ({ name }) => !overflowingActions.includes(name),
              )

              const isLastVisible =
                visibleActions.findIndex((action) => action.name === name) ===
                visibleActions.length - 1

              if (!isLastVisible) {
                actionItemWidth += GAP
              }

              if (withSeparator) {
                actionItemWidth += SEPARATOR_WIDTH + GAP
              }

              let actionsWidth = contentWidth + actionItemWidth

              if (overflowingActions.length) {
                actionsWidth += MORE_ACTION_WIDTH + GAP
              }

              if (actionsWidth > containerRect.width) {
                newOverflowingActions.push(name)
              }

              contentWidth += actionItemWidth

              return {
                contentWidth,
                newOverflowingActions,
              }
            },
            { contentWidth: 0, newOverflowingActions: [] },
          )

          if (isEqual(overflowingActions, newOverflowingActions)) {
            return
          }

          setOverflowingActions(newOverflowingActions)
        }
      }
    }

    const resizeObserver = new ResizeObserver(updateOverflowingActions)

    if (container) {
      resizeObserver.observe(container)
    }

    return () => {
      if (container) {
        resizeObserver.unobserve(container)
      }
    }
  }, [actions, containerRef, overflowingActions])

  const visible = actions.filter(
    (action) => !overflowingActions.includes(action.name),
  )
  const overflowing = actions.filter((action) =>
    overflowingActions.includes(action.name),
  )

  return {
    visible,
    overflowing,
    containerRef,
  }
}
